import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { catchError, EMPTY, map, switchMap, take, tap, throwError } from 'rxjs';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IPasswordPolicies } from '@senet-uiverse/ui';
import { decodeJWT } from '@senet-uiverse/common';
import { injectQueryClient } from '@ngneat/query';

import { ILoginData, IStorageUserData, ILinkedAccountInfo } from '../types';

import { StorageService } from './storage.service';

export const PASSWORD_POLICY_TRANSLATIONS = {
  specialChars: marker('password-policy.min-special-chars'),
  notUsername: marker('password-policy.not-username'),
  lowerCase: marker('password-policy.min-lower-case'),
  upperCase: marker('password-policy.min-upper-case'),
  minLength: marker('password-policy.min-length'),
  notEmail: marker('password-policy.not-email'),
  digits: marker('password-policy.min-digits'),
};

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly storageService = inject(StorageService);
  private readonly queryClient = injectQueryClient();
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  getPasswordPolicies() {
    return this.http.get<IPasswordPolicies>('auth/password-policies');
  }

  login(data: Readonly<ILoginData>) {
    return this.http.post<IStorageUserData>('auth/login', data);
  }

  checkEmailExist(email: Readonly<string>) {
    return this.http
      .get<{ exists: boolean }>('/auth/check-email-exists', {
        params: { email },
      })
      .pipe(map((response) => response.exists));
  }

  checkPhoneExist(phone: Readonly<string>) {
    return this.http
      .get<{ exists: boolean }>('/auth/check-phone-exists', {
        params: { phone },
      })
      .pipe(map((response) => response.exists));
  }

  sendCodeForPhone(organizationId: Readonly<string>, phone: Readonly<string>) {
    return this.http.post<void>(`organization/${organizationId}/send_phone_verification_code`, { phone });
  }

  validatePhoneCode(data: Readonly<{ phone: string; verification_code: string }>) {
    return this.http.post<void>('organization/verify_phone_code', data);
  }

  sendVerificationCodeToEmail(email: Readonly<string>) {
    return this.http.post<void>('auth/send-email-verification-code', { email });
  }

  validateVerificationCodeToEmail(data: Readonly<{ email: string; code: string }>) {
    return this.http.post<boolean>('auth/verify-email-code', data);
  }

  validateKeycloakFieldExistence(value: Readonly<string>, valueType: Readonly<string>) {
    return this.http.post<{ exists: boolean }>('keycloak_data/update/validate-existence', {
      value,
      value_type: valueType,
    });
  }

  updateKeycloakField(value: Readonly<string>, valueType: Readonly<string>) {
    return this.http.post<void>('keycloak_data/update', { value, value_type: valueType });
  }

  fillMissingKeycloakData(data: Readonly<{ email: string } | { phone: string }>) {
    return this.http.post('auth/fill-missing-keycloak-data', data).pipe(switchMap(() => this.refreshToken()));
  }

  refreshToken() {
    const token = this.storageService.storage().user?.keycloak_refresh_token;

    return this.http.post<IStorageUserData>('auth/refresh', { token }).pipe(
      tap((response) => this.storageService.update({ user: response })),
      catchError((e) => {
        this._logout();

        return throwError(() => e);
      }),
    );
  }

  changePassword(data: Readonly<{ current_password: string; new_password: string }>) {
    return this.http.post<void>('auth/change-password', data);
  }

  getLinkedAccounts() {
    return this.http.get<ILinkedAccountInfo[]>('organization/account_info/all');
  }

  removeAccount() {
    return this.http.delete<void>('idm/').pipe(tap(() => this.logout()));
  }

  logout() {
    const token = this.storageService.storage().user?.keycloak_refresh_token;

    this.http
      .post<{ detail: string }>('auth/logout', { token })
      .pipe(
        take(1),
        tap(() => this._logout()),
        catchError(() => {
          this._logout();

          return EMPTY;
        }),
      )
      .subscribe();
  }

  getUserKeycloakId(userData: IStorageUserData | null): string | null {
    if (!userData) {
      return null;
    }

    if (userData.keycloak_user_id) {
      return userData.keycloak_user_id;
    }

    if (userData.keycloak_access_token) {
      const data = decodeJWT<{ sub?: string }>(userData.keycloak_access_token);

      return data?.sub || null;
    }

    return null;
  }

  private _logout() {
    this.queryClient.clear();
    this.storageService.clear();
    this.router.navigateByUrl('/sign-in');
  }
}
